import { useState, type Dispatch, type SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../styling/stylingHelper';
import type { ReportRecipients, ReportRecipientStaff } from '../../../../services/admin/adminService';
import { RESTAURANT_ROLES } from '../../../../services/staff/staffService';
import { INTERNAL_ROLES } from '../../../../services/admin/communicationAdminService';

type TFn = TFunction;
export interface RecipientStaffWithVenues extends ReportRecipientStaff {
  venueNames?: string[];
}

/** Chip value standing for "internal role never set" in the recipient pre-select. */
const INTERNAL_ROLE_UNSET = 'unset';

const INTERNAL_ROLE_LABEL_KEYS: Record<string, string> = {
  culinary_cloud_user: 'adminTesting.generateReport.internalRoleCulinaryCloud',
  app_only_user: 'adminTesting.generateReport.internalRoleAppOnly',
  [INTERNAL_ROLE_UNSET]: 'adminTesting.generateReport.internalRoleUnset',
};

/** Falls back to the raw value for an internal role the frontend copy of the enum doesn't know. */
const internalRoleLabel = (t: TFn, value: string | null): string => {
  const key = INTERNAL_ROLE_LABEL_KEYS[value ?? INTERNAL_ROLE_UNSET];
  return key ? t(key) : (value as string);
};

/** Chip in the recipient pre-select. Red to match the rest of the real-send UI. */
const recipientChipClass = (active: boolean) =>
  cn(
    'px-2 py-0.5 rounded-full border text-xs transition-colors',
    active
      ? 'bg-red-600 dark:bg-red-600 border-red-600 dark:border-red-600 text-white dark:text-white'
      : cn(
          'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
          'hover:bg-gray-50 dark:hover:bg-gray-600',
          secondaryText
        )
  );

/** Recipient pre-select matching: chips are OR within a group, AND across the two groups. */
const staffMatchesRecipientFilters = (
  staff: ReportRecipientStaff,
  roleFilters: string[],
  internalFilters: string[]
): boolean => {
  const roleMatches =
    roleFilters.length === 0 || (!!staff.restaurant_role && roleFilters.includes(staff.restaurant_role));
  const internalMatches =
    internalFilters.length === 0 || internalFilters.includes(staff.internal_role ?? INTERNAL_ROLE_UNSET);
  return roleMatches && internalMatches;
};

/** Manual picks only: all selected people receive the caller's chosen report bundle. */
export function ReportRecipientPicker({ recipients, selectedEmails, onChange, t }: {
  recipients: Omit<ReportRecipients, 'staff'> & { staff: RecipientStaffWithVenues[] };
  selectedEmails: string[];
  onChange: Dispatch<SetStateAction<string[]>>;
  t: TFn;
}) {
  // Role chips that pre-select staff. They are a selection SETTER, not a live filter: changing a
  // chip re-derives the ticked staff, after which manual ticks stand until the next chip change.
  const [reportFileRoleFilters, setReportFileRoleFilters] = useState<string[]>([]);
  const [reportFileInternalRoleFilters, setReportFileInternalRoleFilters] = useState<string[]>([]);
  const toggleReportFileRecipient = (email: string) => {
    onChange(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  /**
   * Re-derive the ticked STAFF from the role chips. The owner's tick is carried over untouched —
   * chips only ever speak about staff. With no chip active nothing is derived, so the very first
   * state of the card (owner pre-ticked only) is left exactly as it was.
   */
  const applyReportFileRoleFilters = (roleFilters: string[], internalFilters: string[]) => {
    setReportFileRoleFilters(roleFilters);
    setReportFileInternalRoleFilters(internalFilters);
    if (roleFilters.length === 0 && internalFilters.length === 0) return;
    const ownerEmail = recipients?.owner?.email ?? null;
    const matchedStaffEmails = (recipients?.staff ?? [])
      .filter(s => staffMatchesRecipientFilters(s, roleFilters, internalFilters))
      .map(s => s.email);
    onChange(prev => {
      // Dedup against the owner only when the owner is actually ticked — a staff row that
      // happens to share the owner's email must still be selectable by chip otherwise.
      const ownerTicked = ownerEmail !== null && prev.includes(ownerEmail);
      const staffEmails = ownerTicked ? matchedStaffEmails.filter(email => email !== ownerEmail) : matchedStaffEmails;
      return ownerTicked ? [ownerEmail, ...staffEmails] : staffEmails;
    });
  };

  const toggleReportFileRoleFilter = (role: string) => {
    const next = reportFileRoleFilters.includes(role)
      ? reportFileRoleFilters.filter(r => r !== role)
      : [...reportFileRoleFilters, role];
    applyReportFileRoleFilters(next, reportFileInternalRoleFilters);
  };

  const toggleReportFileInternalRoleFilter = (internalRole: string) => {
    const next = reportFileInternalRoleFilters.includes(internalRole)
      ? reportFileInternalRoleFilters.filter(r => r !== internalRole)
      : [...reportFileInternalRoleFilters, internalRole];
    applyReportFileRoleFilters(reportFileRoleFilters, next);
  };

  return (
    <>
      {recipients.staff.length > 0 && (
        <div className="mb-2 space-y-1.5">
          <div>
            <span className={cn(secondaryText, 'text-xs block mb-1')}>
              {t('adminTesting.generateReport.recipientsFilterRolesLabel')}
            </span>
            <div className="flex flex-wrap gap-1">
              {RESTAURANT_ROLES.map(role => (
                <button
                  key={role}
                  type="button"
                  onClick={() => toggleReportFileRoleFilter(role)}
                  className={recipientChipClass(reportFileRoleFilters.includes(role))}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className={cn(secondaryText, 'text-xs block mb-1')}>
              {t('adminTesting.generateReport.recipientsFilterInternalLabel')}
            </span>
            <div className="flex flex-wrap gap-1">
              {[...INTERNAL_ROLES, INTERNAL_ROLE_UNSET].map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleReportFileInternalRoleFilter(value)}
                  className={recipientChipClass(reportFileInternalRoleFilters.includes(value))}
                >
                  {internalRoleLabel(t, value === INTERNAL_ROLE_UNSET ? null : value)}
                </button>
              ))}
            </div>
          </div>
          <p className={cn(secondaryText, 'text-xs')}>
            {t('adminTesting.generateReport.recipientsFilterHint')}
          </p>
        </div>
      )}
      <div className="border border-red-200 dark:border-red-800 rounded-lg divide-y divide-red-100 dark:divide-red-900/50 max-h-48 overflow-y-auto">
        {recipients?.owner && (
          <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedEmails.includes(recipients.owner.email)}
              onChange={() => toggleReportFileRecipient(recipients!.owner!.email)}
              className="w-4 h-4 accent-red-600"
            />
            <span className="flex-1 min-w-0">
              <span className={cn(primaryText, smallTextSize, semiBoldText, 'block truncate')}>
                {recipients.owner.name || recipients.owner.email}
              </span>
              <span className={cn(secondaryText, 'text-xs block truncate')}>{recipients.owner.email}</span>
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 shrink-0">
              {t('adminTesting.generateReport.recipientsAdmin')}
            </span>
          </label>
        )}
        {recipients?.staff.map(s => (
          <label key={s.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedEmails.includes(s.email)}
              onChange={() => toggleReportFileRecipient(s.email)}
              className="w-4 h-4 accent-red-600"
            />
            <span className="flex-1 min-w-0">
              <span className={cn(primaryText, smallTextSize, 'block truncate')}>{s.name || s.email}</span>
              <span className={cn(secondaryText, 'text-xs block truncate')}>{s.email}</span>
              {s.venueNames?.length ? <span className={cn(secondaryText, 'text-xs block break-words')}>{s.venueNames.join(', ')}</span> : null}
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {s.internal_role && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {internalRoleLabel(t, s.internal_role)}
                </span>
              )}
              {s.restaurant_role ? (
                <span className={cn(secondaryText, 'text-xs')}>{s.restaurant_role}</span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  {t('adminTesting.generateReport.recipientsNoRole')}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
    </>
  );
}
